import { useCallback, useEffect, useState } from 'react'

interface BuildInfo {
  version: string
  builtAt?: string
}

// How often to poll for a new version while the tab is open/visible.
const CHECK_INTERVAL_MS = 10 * 60 * 1000 // 10 minutes

async function fetchBuildInfo(): Promise<BuildInfo | null> {
  try {
    const response = await fetch(`/build-info.json?t=${Date.now()}`, {
      cache: 'no-store',
    })
    if (!response.ok) return null
    return (await response.json()) as BuildInfo
  } catch {
    // Offline / transient network issue — just try again on the next tick.
    return null
  }
}

/**
 * Polls a small, uncached `build-info.json` file (regenerated on every
 * deploy) and reports when its version no longer matches the one this tab
 * loaded with. Used to show a "new version available, refresh when ready"
 * banner instead of letting the user hit a broken lazy-loaded route later.
 *
 * Deliberately does NOT reload automatically — the user decides when to
 * refresh, so in-progress form work isn't lost mid-edit.
 */
export function useAppVersionCheck() {
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null)

  const checkForUpdate = useCallback(async () => {
    const info = await fetchBuildInfo()
    if (!info) return

    // Compare against the version baked into this bundle at build time
    // (__APP_VERSION__), not the first poll's result. This avoids a race where
    // a deploy landing between page load and the first fetch would otherwise
    // establish the *new* version as the baseline and never surface the banner.
    if (info.version !== __APP_VERSION__) {
      setLatestVersion(info.version)
    }
  }, [])

  useEffect(() => {
    // Skip in local dev: there's no build-info.json until `npm run build`
    // has produced one, and hot-reloading makes this check meaningless.
    if (import.meta.env.DEV) return

    checkForUpdate()

    const interval = setInterval(checkForUpdate, CHECK_INTERVAL_MS)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [checkForUpdate])

  const updateAvailable =
    latestVersion !== null && latestVersion !== dismissedVersion

  const dismiss = useCallback(() => {
    if (latestVersion) setDismissedVersion(latestVersion)
  }, [latestVersion])

  const refresh = useCallback(() => {
    window.location.reload()
  }, [])

  return { updateAvailable, dismiss, refresh }
}
